import React, { ChangeEventHandler, FormEventHandler, useState } from 'react'
import log from 'loglevel'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  CONTROL_STATUS,
  ControlStatusValue,
  CreateUpdateControlRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { useInvalidateControlsCache } from 'src/components/async/model/control'
import { useThrobberContext } from 'src/Throbber'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useCurrentControl } from 'src/context/FormContext'
import { ModalForm, ModalFormId } from '../../../components/ModalForms'
import { DerivedResizableTextArea } from '../../../components/Reusable/Inputs/TextInput'
import { ERROR_MARKING_CONTROL_NA } from './ControlModals.constants'
import { MarkNotApplicableDescription } from './ControlModals.components'

export const MarkNotApplicableModal = (): JSX.Element => {
  const { startThrobber, stopThrobber } = useThrobberContext()
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const mutate = useInvalidateControlsCache()
  const show = useIsShowModal(ModalFormId.MARK_CONTROL_NA)
  const { currentControl } = useCurrentControl()
  const [naReason, setNaReason] = useState<string>('')
  const [isValidReason, setIsValidReason] = useState<boolean>(true)
  const [isDisabled, setIsDisabled] = useState<boolean>(true)

  const onHide = () => {
    setNaReason('')
    setIsValidReason(true)
  }
  const hide = useHideModal({
    modalId: ModalFormId.MARK_CONTROL_NA,
    onHide,
  })

  const markControlNA: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    if (!currentControl.id) {
      throw new Error('No control id provided')
    }
    try {
      startThrobber()
      const req = new CreateUpdateControlRequest()
        .setId(currentControl.id)
        .setStatus(
          new ControlStatusValue().setValue(CONTROL_STATUS.NOTAPPLICABLE),
        )
        .setNotApplicableReason(new StringValue().setValue(naReason))
      await modelClient.createOrUpdateControl(req)
      mutate()
      hide()
    } catch (error) {
      hide()
      showInfoToast(ERROR_MARKING_CONTROL_NA)
      log.error(
        `Error marking control with id ${currentControl.id} not applicable`,
        error,
      )
    } finally {
      stopThrobber()
    }
  }

  const changeNaReason: ChangeEventHandler<HTMLInputElement> = (e) => {
    const newReason = e.currentTarget.value.trim()
    if (!isValidReason) {
      setIsValidReason(true)
    }
    setIsDisabled(!(newReason.length > 0))
    setNaReason(e.currentTarget.value)
  }

  return (
    <ModalForm
      show={show}
      hide={hide}
      title="Mark this control Not Applicable?"
      submitText="Mark Not Applicable"
      description={<MarkNotApplicableDescription />}
      formId={ModalFormId.MARK_CONTROL_NA}
      showOverflow
      disableSubmitButton={isDisabled}
    >
      <form id={ModalFormId.MARK_CONTROL_NA} onSubmit={markControlNA}>
        <DerivedResizableTextArea
          label="Reason this Control is Not Applicable"
          name="input"
          initVal={naReason}
          placeholder="Write something here so your auditor understands why this control isn't relevant for your business"
          form={ModalFormId.MARK_CONTROL_NA}
          customOnChange={changeNaReason}
        />
      </form>
    </ModalForm>
  )
}
